import React, { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';
import hljs from 'highlight.js';
import { destroyScrollbars, initializeScrollbars } from './../../composables/scrollBar.js';

function Widgets() {
	const rootRef = useRef(null);

	useEffect(() => {
		const root = rootRef.current;
		if (!root) return undefined;

		const stopLinks = (event) => {
			const link = event.target.closest && event.target.closest('a[href="#/"]');
			if (link) event.preventDefault();
			const scrollLink = event.target.closest && event.target.closest('[data-toggle="scroll-to"]');
			if (scrollLink) {
				event.preventDefault();
				const target = root.querySelector(scrollLink.getAttribute('href'));
				if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}
		};
		root.addEventListener('click', stopLinks);

		const scrollspy = root.querySelector('[data-init="scrollspy"]');
		const scrollspyLinks = scrollspy ? Array.from(scrollspy.querySelectorAll('a[data-toggle="scroll-to"]')) : [];
		const scrollspyItems = scrollspyLinks
			.map((link) => ({ link, section: root.querySelector(link.getAttribute('href')) }))
			.filter(({ section }) => section);
		let scrollspyFrame = null;
		let scrollContainer = window;
		let scrollParent = root.parentElement;

		while (scrollParent && scrollParent !== document.body) {
			const overflowY = getComputedStyle(scrollParent).overflowY;
			if (overflowY === 'auto' || overflowY === 'scroll') {
				scrollContainer = scrollParent;
				break;
			}
			scrollParent = scrollParent.parentElement;
		}

		const updateScrollspy = () => {
			const containerTop = scrollContainer === window ? 0 : scrollContainer.getBoundingClientRect().top;
			const activeItem = scrollspyItems.reduce((active, item) => {
				const sectionTop = item.section.getBoundingClientRect().top;
				return sectionTop <= containerTop + 120 ? item : active;
			}, scrollspyItems[0]);

			if (activeItem) {
				scrollspyLinks.forEach((link) => link.classList.toggle('active', link === activeItem.link));
			}
		};
		const onScroll = () => {
			if (scrollspyFrame === null) {
				scrollspyFrame = window.requestAnimationFrame(() => {
					scrollspyFrame = null;
					updateScrollspy();
				});
			}
		};

		if (scrollspyItems.length) {
			scrollContainer.addEventListener('scroll', onScroll, { passive: true });
			updateScrollspy();
		}

		const scrollbars = initializeScrollbars(root);

		Array.from(root.querySelectorAll('code[data-url]')).forEach(async (element) => {
			try {
				const response = await fetch(element.getAttribute('data-url'));
				if (response.ok) {
					element.textContent = await response.text();
					element.classList.remove('xml');
					element.classList.add('language-typescript');
					hljs.highlightElement(element);
				}
			} catch (error) {
				element.textContent = '';
			}
		});

		const canvas = root.querySelector('#barChart');
		let chart = null;
		if (canvas) {
			const css = (name, fallback) => getComputedStyle(document.body).getPropertyValue(name).trim() || fallback;
			const bodyColor = css('--bs-body-color', '#2d353c');
			const componentColor = css('--bs-component-color', bodyColor);
			const componentBg = css('--bs-component-bg', '#ffffff');
			const light = css('--bs-light', '#f2f3f4');
			const dark = css('--bs-dark', '#2d353c');
			const secondary = css('--bs-secondary', '#6c757d');
			chart = new Chart(canvas, {
				type: 'bar',
				data: {
					labels: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
					datasets: [
						{
							label: 'Total Visitors',
							data: [37, 31, 36, 34, 43, 31, 50],
							backgroundColor: dark,
							borderColor: 'transparent'
						},
						{
							label: 'Returning Visitors',
							data: [27, 21, 16, 14, 23, 11, 30],
							backgroundColor: secondary,
							borderColor: 'transparent'
						}
					]
				},
				options: {
					responsive: true,
					maintainAspectRatio: false,
					plugins: {
						legend: { labels: { color: bodyColor } },
						tooltip: { titleColor: componentBg, bodyColor: componentBg, backgroundColor: componentColor }
					},
					scales: {
						x: { ticks: { color: bodyColor }, grid: { color: light } },
						y: { beginAtZero: true, ticks: { color: bodyColor }, grid: { color: light } }
					}
				}
			});
		}

		return () => {
			root.removeEventListener('click', stopLinks);
			if (scrollspyItems.length) scrollContainer.removeEventListener('scroll', onScroll);
			if (scrollspyFrame !== null) window.cancelAnimationFrame(scrollspyFrame);
			destroyScrollbars(scrollbars);
			if (chart) chart.destroy();
		};
	}, []);

	return (
		<div ref={rootRef}>
			<div className="container">
				<div className="row justify-content-center">
					<div className="col-xl-10">
						<div className="row">
							<div className="col-xl-9">
								<h1 className="page-header">
									Widgets <small>page header description goes here...</small>
								</h1>
								<hr className="mb-4" />
								<div id="cardWidget" className="mb-5">
									<h4>Card widget</h4>
									<p>
										Card widget is created by using existing Bootstrap <code>.card</code> component
										with <code>.card-img</code>, <code>.card-img-overlay</code> and{' '}
										<code>.d-flex</code> utilities.
									</p>
									<div className="card">
										<div className="card-body">
											<div className="row">
												<div className="col-xl-8">
													<div
														className="card border-0 overflow-hidden rounded-3"
														data-bs-theme="dark"
													>
														<div className="h-250px d-flex align-items-center">
															<img
																src="/color-admin/admin/react/assets/img/widget/cover-1.jpg"
																alt=""
																className="card-img"
															/>
														</div>
														<div className="card-img-overlay d-flex flex-column bg-black bg-opacity-50 rounded">
															<div className="flex-fill">
																<div className="d-flex align-items-center">
																	<h6 className="m-0">Youtube</h6>
																	<div className="dropdown ms-auto">
																		<a
																			href="#/"
																			className="text-white"
																			data-bs-toggle="dropdown"
																		>
																			<i className="fa fa-ellipsis-h"></i>
																		</a>
																		<div className="dropdown-menu dropdown-menu-end">
																			<a
																				href="//www.youtube.com/watch?v=_AS5nu4u1ss"
																				className="dropdown-item"
																			>
																				View
																			</a>
																		</div>
																	</div>
																</div>
															</div>
															<div>
																<a
																	href="//www.youtube.com/watch?v=_AS5nu4u1ss"
																	data-lity
																	className="text-white text-decoration-none d-flex align-items-center"
																>
																	<div className="bg-theme-gradient w-50px h-50px rounded-3 d-flex align-items-center justify-content-center">
																		<i className="fa fa-play fa-lg"></i>
																	</div>
																	<div className="ms-3 flex-1">
																		<div className="fw-bold">
																			New Videos - Behind The Forest Tours
																		</div>
																		<div className="small fw-semibold d-flex gap-3 text-body text-opacity-75">
																			<div className="d-flex gap-1 align-items-center">
																				<iconify-icon
																					icon="solar:eye-bold"
																					className="fs-6 my-n1"
																				></iconify-icon>{' '}
																				892 views{' '}
																			</div>
																			<div className="d-flex gap-1 align-items-center">
																				<iconify-icon
																					icon="solar:clock-circle-bold"
																					className="fs-6 my-n1"
																				></iconify-icon>{' '}
																				39min ago
																			</div>
																		</div>
																	</div>
																</a>
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
										<div
											className="hljs-container hljs-wrapper rounded-0 rounded-bottom"
											data-bs-theme="dark"
										>
											<pre>
												<code
													className="xml"
													data-url="/color-admin/admin/react/assets/data/widgets/code-1.json"
												></code>
											</pre>
										</div>
									</div>
								</div>
								<div id="listWidget" className="mb-5">
									<h4>List widget</h4>
									<p>
										List widget is created by using existing Bootstrap <code>.list-group</code>{' '}
										component with <code>.d-flex</code> and droplet utilities css classes.
									</p>
									<div className="card">
										<div className="card-body">
											<div className="row">
												<div className="col-xl-6">
													<div className="text-body mb-2 fw-bold">With icon</div>
													<div className="list-group mb-3 rounded-3">
														<div className="list-group-item d-flex align-items-center">
															<div className="w-40px h-40px d-flex align-items-center justify-content-center bg-primary text-white rounded-3">
																<iconify-icon
																	icon="solar:cassette-bold-duotone"
																	className="fa-2x"
																></iconify-icon>
															</div>
															<div className="flex-fill px-3 py-1">
																<div className="fw-bold">Apps Store</div>
																<div className="small text-secondary fw-semibold">
																	102 new download
																</div>
															</div>
															<div className="dropdown">
																<a
																	href="#/"
																	data-bs-toggle="dropdown"
																	className="link-muted"
																>
																	<i className="fa fa-ellipsis-h fa-lg"></i>
																</a>
																<div className="dropdown-menu dropdown-menu-end">
																	<a href="#/" className="dropdown-item">
																		View
																	</a>
																	<a href="#/" className="dropdown-item">
																		Analytics
																	</a>
																	<a href="#/" className="dropdown-item">
																		Report
																	</a>
																</div>
															</div>
														</div>
														<div className="list-group-item d-flex align-items-center">
															<div className="w-40px h-40px d-flex align-items-center justify-content-center bg-orange text-white rounded-3">
																<iconify-icon
																	icon="solar:book-2-bold-duotone"
																	className="fa-2x"
																></iconify-icon>
															</div>
															<div className="flex-fill px-3 py-1">
																<div className="fw-bold">iBooks App</div>
																<div className="small text-secondary fw-semibold">
																	32 new download
																</div>
															</div>
															<div className="dropdown">
																<a
																	href="#/"
																	data-bs-toggle="dropdown"
																	className="link-muted"
																>
																	<i className="fa fa-ellipsis-h fa-lg"></i>
																</a>
																<div className="dropdown-menu dropdown-menu-end">
																	<a href="#/" className="dropdown-item">
																		View
																	</a>
																	<a href="#/" className="dropdown-item">
																		Analytics
																	</a>
																	<a href="#/" className="dropdown-item">
																		Report
																	</a>
																</div>
															</div>
														</div>
													</div>
													<div className="text-body mb-2 fw-bold">With image</div>
													<div className="list-group mb-3 rounded-3">
														<a
															href="#/"
															className="list-group-item list-group-item-action d-flex align-items-center text-body"
														>
															<div className="w-40px h-40px d-flex align-items-center justify-content-center">
																<img
																	src="/color-admin/admin/react/assets/img/user/profile.jpg"
																	alt=""
																	className="h-100 w-100 rounded-circle object-fit-cover"
																/>
															</div>
															<div className="flex-fill ps-3">
																<div className="fw-semibold d-flex align-items-center">
																	Isaiah Hughes{' '}
																	<span className="fa fa-circle text-green fs-6px ms-2"></span>
																</div>
															</div>
														</a>
														<a
															href="#/"
															className="list-group-item list-group-item-action d-flex align-items-center text-body"
														>
															<div className="w-40px h-40px d-flex align-items-center justify-content-center">
																<img
																	src="/color-admin/admin/react/assets/img/user/user-2.jpg"
																	alt=""
																	className="h-100 w-100 rounded-circle object-fit-cover"
																/>
															</div>
															<div className="flex-fill ps-3">
																<div className="fw-bold d-flex align-items-center">
																	Ryan Turner{' '}
																	<span className="fa fa-circle text-body text-opacity-50 fs-6px ms-2"></span>
																</div>
															</div>
														</a>
													</div>
												</div>
												<div className="col-xl-6">
													<div className="text-body mb-2 fw-bold">With settings</div>
													<div className="list-group rounded-3">
														<div className="list-group-item d-flex align-items-center">
															<div className="flex-fill py-1">
																<div className="fw-bold">Server auto backup</div>
																<div className="fw-semibold small text-secondary">
																	last backup since yesterday
																</div>
															</div>
															<div>
																<div className="form-check me-n1">
																	<input
																		type="checkbox"
																		className="form-check-input"
																		id="customSwitch1"
																		checked
																	/>
																	<label
																		className="form-check-label"
																		htmlFor="customSwitch1"
																	></label>
																</div>
															</div>
														</div>
														<div className="list-group-item d-flex align-items-center">
															<div className="flex-fill py-1">
																<div className="fw-bold">Analytics enabled</div>
																<div className="fw-semibold small text-secondary">
																	3,392 data collected
																</div>
															</div>
															<div>
																<div className="form-switch me-n1">
																	<input
																		type="checkbox"
																		className="form-check-input"
																		id="customSwitch2"
																	/>
																	<label
																		className="form-check-label"
																		htmlFor="customSwitch2"
																	></label>
																</div>
															</div>
														</div>
														<a
															href="#/"
															className="list-group-item list-group-item-action d-flex align-items-center"
														>
															<div className="flex-fill py-1">
																<div className="fw-bold">Link with arrow</div>
															</div>
															<div>
																<i className="fa fa-chevron-right"></i>
															</div>
														</a>
														<a
															href="#/"
															className="list-group-item list-group-item-action d-flex align-items-center"
														>
															<div className="flex-fill py-1">
																<div className="fw-bold">Link with arrow</div>
															</div>
															<div>
																<i className="fa fa-chevron-right"></i>
															</div>
														</a>
													</div>
												</div>
											</div>
										</div>
										<div
											className="hljs-container hljs-wrapper rounded-0 rounded-bottom"
											data-bs-theme="dark"
										>
											<pre>
												<code
													className="xml"
													data-url="/color-admin/admin/react/assets/data/widgets/code-2.json"
												></code>
											</pre>
										</div>
									</div>
								</div>
								<div id="statsWidget" className="mb-5">
									<h4>Stats widget</h4>
									<p>
										States widget is created by using Bootstrap <code>.card</code> component with{' '}
										<code>.d-flex</code> and <code>background-color</code> utilities.
									</p>
									<div className="card">
										<div className="card-body">
											<div className="row g-3 mb-3">
												<div className="col-lg-3 col-md-4 col-6 d-flex flex-column gap-3">
													<div className="card bg-primary bg-opacity-15 border-0 rounded-3 p-3">
														<div className="fw-bold mb-1 text-truncate">Total Sales</div>
														<div className="h5 mb-1  text-primary text-truncate">
															RM 82,300
														</div>
														<div className="small fw-semibold text-truncate">
															+12% this week
														</div>
													</div>
													<div className="card bg-orange bg-opacity-15 border-0 rounded-3 p-3">
														<div className="fw-bold mb-1 text-truncate">New Users</div>
														<div className="h5 mb-1 text-orange text-truncate">1,245</div>
														<div className="small fw-semibold text-truncate">
															+320 vs last week
														</div>
													</div>
												</div>
												<div className="col-lg-3 col-md-4 col-6 d-flex flex-column gap-3">
													<div className="card bg-success bg-opacity-15 border-0 rounded-3 p-3">
														<div className="fw-bold mb-1 text-truncate">
															Conversion Rate
														</div>
														<div className="h5 mb-1 text-success text-truncate">4.8%</div>
														<div className="small fw-semibold text-truncate">
															Steady growth
														</div>
													</div>
													<div className="card bg-indigo bg-opacity-15 border-0 rounded-3 p-3">
														<div className="fw-bold mb-1 text-truncate">Bounce Rate</div>
														<div className="h5 mb-1 text-indigo text-truncate">32%</div>
														<div className="small fw-semibold text-truncate">
															-5% vs prev month
														</div>
													</div>
												</div>
												<div className="col-lg-6">
													<div className="card h-100 rounded-3 bg-light border-0">
														<div className="card-body">
															<div className="mb-3 fw-bold d-flex align-items-center">
																<div className="flex-fill">Users Activity</div>
																<div>
																	<a
																		href="#/"
																		data-bs-toggle="dropdown"
																		className="link-muted"
																	>
																		<i className="fa fa-ellipsis-h fa-lg"></i>
																	</a>
																	<div
																		className="dropdown-menu dropdown-menu-end"
																		data-bs-theme="light"
																	>
																		<a href="#/" className="dropdown-item">
																			View full report
																		</a>
																		<a href="#/" className="dropdown-item">
																			Reload
																		</a>
																		<a href="#/" className="dropdown-item">
																			Export Data
																		</a>
																	</div>
																</div>
															</div>
															<div className="row g-0 text-center mb-3">
																<div className="col-6 border-end">
																	<div className="fs-2 lh-1 text-primary mb-1">
																		<iconify-icon icon="solar:users-group-rounded-bold-duotone"></iconify-icon>
																	</div>
																	<h3 className="fw-bolder mb-1">450</h3>
																	<div className="fw-semibold small text-secondary">
																		Active Now
																	</div>
																</div>
																<div className="col-6 ps-4">
																	<div className="fs-2 lh-1 text-primary mb-1">
																		<iconify-icon icon="solar:login-2-bold-duotone"></iconify-icon>
																	</div>
																	<h3 className="fw-bolder mb-1">1,820</h3>
																	<div className="fw-semibold small text-secondary">
																		Daily Logins
																	</div>
																</div>
															</div>
															<div className="mb-1">
																<div className="fw-semibold small mb-1">
																	Active Ratio (20%)
																</div>
																<div className="progress h-6px">
																	<div
																		className="progress-bar bg-dark"
																		style={{ width: '20%' }}
																	></div>
																</div>
															</div>
															<div className="small text-muted mb-n2">Real-time data</div>
														</div>
													</div>
												</div>
											</div>
											<div className="row g-3 mb-3">
												<div className="col-xl-6">
													<div className="card rounded-3 overflow-hidden h-100 ">
														<div className="card-body">
															<div className="mb-2 fw-bold d-flex align-items-center">
																<div className="flex-fill">Total Orders</div>
																<div>
																	<a
																		href="#/"
																		data-bs-toggle="dropdown"
																		className="link-muted"
																	>
																		<i className="fa fa-ellipsis-h fa-lg"></i>
																	</a>
																	<div
																		className="dropdown-menu dropdown-menu-end"
																		data-bs-theme="light"
																	>
																		<a href="#/" className="dropdown-item">
																			View full report
																		</a>
																		<a href="#/" className="dropdown-item">
																			Reload
																		</a>
																		<a href="#/" className="dropdown-item">
																			Export Data
																		</a>
																	</div>
																</div>
															</div>
															<div className="row mb-1">
																<div className="col-lg-12 position-relative">
																	<h1 className="mb-0 d-flex align-items-center">
																		302
																		<span className="fs-10px badge bg-warning text-white rounded-pill pe-2 ps-1 py-6px d-flex align-items-center ms-3">
																			<i className="fa fa-exclamation-circle fa-fw fa-lg me-1 opacity-5"></i>
																			3 pending orders
																		</span>
																	</h1>
																	<div className="fs-6 mt-2 pb-1">
																		<span className="badge bg-green text-white py-4px me-1 fw-bold">
																			+12%
																		</span>{' '}
																		<small className="fw-semibold text-secondary">
																			compare to last week
																		</small>
																	</div>
																</div>
															</div>
															<hr className="mt-3 mb-2" />
															<div className="pt-1 small fw-semibold text-secondary">
																<div className="">last updated on</div>
																<div className="text-body fw-bold">
																	Feb 3, 4:09:57 AM UTC
																</div>
															</div>
														</div>
													</div>
												</div>
												<div className="col-xl-6">
													<div className="card rounded-3 ">
														<div className="card-body">
															<div className="fw-bold mb-3">Task Summary</div>
															<div className="d-flex justify-content-between align-items-center mb-1">
																<div className="fw-semibold d-flex gap-2 align-items-center small">
																	<span className="w-8px h-8px rounded-circle bg-primary"></span>{' '}
																	Completed Tasks
																</div>
																<div className="fw-bold">12</div>
															</div>
															<hr className="my-2" />
															<div className="d-flex justify-content-between align-items-center mb-1">
																<div className="fw-semibold d-flex gap-2 align-items-center small">
																	<span className="w-8px h-8px rounded-circle bg-secondary"></span>{' '}
																	In Progress
																</div>
																<div className="fw-bold">4</div>
															</div>
															<hr className="my-2" />
															<div className="d-flex justify-content-between align-items-center mb-1">
																<div className="fw-semibold d-flex gap-2 align-items-center small">
																	<span className="w-8px h-8px rounded-circle bg-indigo"></span>{' '}
																	Total Items
																</div>
																<div className="fw-bold">16</div>
															</div>
															<div className="mt-3 d-flex align-items-center gap-2">
																<small className="text-secondary fw-semibold">
																	Due this week
																</small>
																<small className="fw-bold">Avg. Time: 4h</small>
															</div>
														</div>
													</div>
												</div>
											</div>
											<div className="row g-3 mb-3">
												<div className="col-lg-6 d-flex flex-column gap-3">
													<div className="card rounded-3 ">
														<div className="card-body d-flex justify-content-between align-items-center">
															<div className="flex-fill">
																<div className="fw-bold mb-1">Total Sales</div>
																<h3 className="mb-0">$12,480</h3>
															</div>
															<iconify-icon
																icon="solar:shop-minimalistic-bold-duotone"
																className="display-5 text-secondary text-opacity-75"
															></iconify-icon>
														</div>
													</div>
													<div className="card rounded-3 ">
														<div className="card-body d-flex justify-content-between align-items-center">
															<div className="flex-fill">
																<div className="fw-bold mb-1">New Customers</div>
																<h3 className="mb-0">324</h3>
															</div>
															<iconify-icon
																icon="solar:user-plus-rounded-bold-duotone"
																className="display-5 text-secondary text-opacity-75"
															></iconify-icon>
														</div>
													</div>
													<div className="card rounded-3 ">
														<div className="card-body">
															<div className="d-flex justify-content-between align-items-center">
																<div>
																	<div className="text-body fw-bold mb-1">
																		Social Engagement
																	</div>
																	<div className="text-body fw-bold fs-2 mb-1">
																		24.5K
																	</div>
																	<div className="d-flex align-items-center gap-1 small">
																		<span className="text-primary fw-bold">
																			+5.2%
																		</span>
																		<span className="text-muted">vs last week</span>
																	</div>
																</div>
																<div className="bg-primary bg-opacity-10 text-primary w-80px h-80px d-flex align-items-center justify-content-center rounded-3">
																	<i className="bi bi-heart"></i>
																</div>
															</div>
															<hr />
															<div className="row text-center">
																<div className="col-4">
																	<div className="fw-bold text-primary">1.2K</div>
																	<div className="fw-semibold text-secondary small">
																		Likes
																	</div>
																</div>
																<div className="col-4 border-start border-end">
																	<div className="fw-bold text-primary">856</div>
																	<div className="fw-semibold text-secondary small">
																		Shares
																	</div>
																</div>
																<div className="col-4">
																	<div className="fw-bold text-primary">324</div>
																	<div className="fw-semibold text-secondary small">
																		Comments
																	</div>
																</div>
															</div>
														</div>
													</div>
													<div
														className="card rounded-3 bg-theme-gradient border-0"
														data-bs-theme="dark"
													>
														<div className="card-body d-flex justify-content-between align-items-center">
															<div>
																<div className="fw-bold text-white mb-1">Revenue</div>
																<h2 className="mb-1">$42,580</h2>
																<div className="fw-semibold small text-white">
																	<i className="fa fa-caret-up"></i> 12.5% increase
																</div>
															</div>
															<div className="display-3 text-white">
																<iconify-icon icon="solar:bag-smile-bold-duotone"></iconify-icon>
															</div>
														</div>
													</div>
													<div className="card border-0 bg-gradient-45 bg-gradient-from-blue bg-gradient-to-indigo rounded-3">
														<div className="card-body d-flex justify-content-between align-items-center">
															<div className="fw-semibold small">
																<div className="text-white text-opacity-50 mb-1">
																	Conversion Rate
																</div>
																<div className="fs-2 fw-bold text-white mb-1">4.8%</div>
																<div className="d-flex gap-4">
																	<div>
																		<div className="text-white text-opacity-50">
																			Previous
																		</div>
																		<div className="text-white fw-bold">3.6%</div>
																	</div>
																	<div>
																		<div className="text-white text-opacity-50">
																			Change
																		</div>
																		<div className="text-white fw-bold">+1.2%</div>
																	</div>
																</div>
															</div>
															<div className="w-80px h-80px bg-white bg-opacity-20 bg-gradient text-white rounded-3 d-flex align-items-center justify-content-center">
																<iconify-icon
																	icon="bi:percent"
																	className="fs-1"
																></iconify-icon>
															</div>
														</div>
													</div>
												</div>
												<div className="col-lg-6 d-flex flex-column gap-3">
													<div className="card rounded-3 ">
														<div className="card-body">
															<div className="d-flex justify-content-between align-items-center">
																<div>
																	<div className="fw-bold text-body mb-1">
																		Project Completion
																	</div>
																	<div className="text-body mb-1 fs-2 fw-bold">
																		78%
																	</div>
																	<div className="fw-semibold text-secondary small">
																		<span className="text-body fw-bold text-decoration-underline">
																			12 of 15
																		</span>{' '}
																		tasks done
																	</div>
																</div>
																<div className="position-relative w-80px h-80px">
																	<svg width="80" height="80" viewBox="0 0 80 80">
																		<circle
																			className="progress-ring-circle"
																			stroke="#0c83f0"
																			stroke-width="8"
																			fill="transparent"
																			r="36"
																			cx="40"
																			cy="40"
																			stroke-dasharray="226.194"
																			stroke-dashoffset="49.7"
																		></circle>
																	</svg>
																	<div className="position-absolute top-50 start-50 translate-middle text-center">
																		<span className="fw-bold fs-5">78%</span>
																	</div>
																</div>
															</div>
														</div>
													</div>
													<div
														className="card rounded-3 "
														style={{ borderTop: '3px solid var(--bs-primary)' }}
													>
														<div className="card-body d-flex justify-content-between align-items-start">
															<div>
																<div className="fw-bold text-body mb-1 d-flex align-items-center gap-2">
																	<span className="w-6px h-6px bg-primary rounded"></span>{' '}
																	Active Users
																</div>
																<div className="text-body fs-2 fw-bold mb-1">8,642</div>
																<div className="d-flex align-items-center gap-1 small">
																	<span className="text-primary fw-bold">+5.2%</span>
																	<span className="text-secondary">vs last week</span>
																</div>
															</div>
															<div className="h-60px my-3">
																<svg width="100" height="70" viewBox="0 0 100 70">
																	<path
																		d="M0,30 L20,25 L40,15 L60,20 L80,10 L100,15"
																		stroke="#0c83f0"
																		stroke-width="3"
																		fill="none"
																		stroke-linecap="round"
																		stroke-linejoin="round"
																	/>
																</svg>
															</div>
														</div>
													</div>
													<div
														className="card rounded-3 "
														style={{ borderTop: '3px solid var(--bs-green)' }}
													>
														<div className="card-body d-flex justify-content-between align-items-center">
															<div className="">
																<div className="text-body mb-1 d-flex align-items-center gap-2 fw-bold">
																	<span className="w-6px h-6px bg-green rounded"></span>{' '}
																	Storage Usage
																</div>
																<div className="text-body fs-2 fw-bold mb-1">65%</div>
																<div className="fw-semibold small text-secondary">
																	<span className="fw-bold text-green">325GB</span> of{' '}
																	<span className="fw-bold text-body">500GB</span>{' '}
																	used
																</div>
															</div>
															<div
																className="w-80px h-80px rounded-circle position-relative"
																style={{
																	background:
																		'conic-gradient(var(--bs-green) 0% 65%, #e0e0e0 65% 100%)'
																}}
															>
																<span className="fs-5 fw-bold position-relative z-1 w-100 h-100 d-flex align-items-center justify-content-center">
																	65%
																</span>
																<div className="w-60px h-60px bg-component rounded-circle position-absolute top-0 start-0 m-10px"></div>
															</div>
														</div>
													</div>
													<div className="card border-0 bg-gradient-135 bg-gradient-from-orange bg-gradient-to-red rounded-3">
														<div className="card-body text-center">
															<div className="text-white fw-bold mb-3">
																Performance Score
															</div>
															<div className="position-relative d-inline-block mb-3">
																<div className="w-100px h-100px">
																	<svg width="100" height="100" viewBox="0 0 100 100">
																		<circle
																			className="progress-ring-circle"
																			stroke="#ffffff"
																			stroke-width="8"
																			fill="transparent"
																			r="45"
																			cx="50"
																			cy="50"
																			stroke-dasharray="282.743"
																			stroke-dashoffset="84.8"
																		></circle>
																	</svg>
																	<div className="position-absolute top-50 start-50 translate-middle text-center">
																		<span className="text-white fw-bold fs-3">
																			70
																		</span>
																	</div>
																</div>
															</div>
															<div className="text-white small mb-3">
																Good • 12 points improvement
															</div>
															<div className="row text-center">
																<div className="col-4">
																	<div className="p-2 rounded-2 bg-black bg-opacity-20">
																		<div className="fw-bold text-white">1.2K</div>
																		<div className="fw-semibold text-white text-opacity-75 small">
																			Likes
																		</div>
																	</div>
																</div>
																<div className="col-4">
																	<div className="p-2 rounded-2 bg-black bg-opacity-20">
																		<div className="fw-bold text-white">856</div>
																		<div className="fw-semibold text-white text-opacity-75 small">
																			Shares
																		</div>
																	</div>
																</div>
																<div className="col-4">
																	<div className="p-2 rounded-2 bg-black bg-opacity-20">
																		<div className="fw-bold text-white">324</div>
																		<div className="fw-semibold text-white text-opacity-75 small">
																			Comments
																		</div>
																	</div>
																</div>
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
										<div
											className="hljs-container hljs-wrapper rounded-0 rounded-bottom"
											data-bs-theme="dark"
										>
											<pre>
												<code
													className="xml"
													data-url="/color-admin/admin/react/assets/data/widgets/code-3.json"
												></code>
											</pre>
										</div>
									</div>
								</div>
								<div id="chartWidget" className="mb-5">
									<h4>Chart widget</h4>
									<p>
										Chart widget created by using Bootstrap <code>.card</code> and{' '}
										<code>.list-group</code> component combined with Chart.js plugins.
									</p>
									<div className="card">
										<div className="card-body">
											<div className="row">
												<div className="col-xl-8">
													<div className="card rounded-3 overflow-hidden ">
														<div className="card-header fw-bold bg-none d-flex align-items-center">
															Weekly Web Analytics
															<span className="badge ms-3 fw-bolder py-6px rounded-1 bg-primary text-white">
																11 Mar - 18 Mar
															</span>
															<a
																href="#/"
																className="ms-auto link-muted text-decoration-none"
																data-toggle="card-expand"
															>
																<i className="fa fa-expand"></i>
															</a>
														</div>
														<div className="card-body text-body text-center">
															<canvas id="barChart" className="h-150"></canvas>
														</div>
														<div className="list-group list-group-flush">
															<div className="list-group-item border-top-0 rounded-top-0 d-flex align-items-center">
																<div className="w-40px h-40px rounded-3 bg-primary text-white d-flex align-items-center justify-content-center">
																	<iconify-icon
																		icon="solar:users-group-rounded-bold-duotone"
																		className="fs-4"
																	></iconify-icon>
																</div>
																<div className="flex-fill px-3 py-1">
																	<div className="fw-bold">Total Visitors</div>
																	<div className="fw-semibold small text-secondary">
																		11 Mar - 18 Mar
																	</div>
																</div>
																<div>
																	<div className="fw-bold h3 m-0">1.3m</div>
																</div>
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
										<div
											className="hljs-container hljs-wrapper rounded-0 rounded-bottom"
											data-bs-theme="dark"
										>
											<pre>
												<code
													className="xml"
													data-url="/color-admin/admin/react/assets/data/widgets/code-4.json"
												></code>
											</pre>
										</div>
									</div>
								</div>
								<div id="userListWidget" className="mb-5">
									<h4>User list widget</h4>
									<p>
										User list widget used to display people who participate in a project or a group.
									</p>
									<div className="card">
										<div className="card-body">
											<div className="widget-user-list">
												<div className="widget-user-list-item">
													<a href="#/" className="widget-user-list-link">
														<img src="/color-admin/admin/react/assets/img/user/user-1.jpg" alt="" />
													</a>
												</div>
												<div className="widget-user-list-item">
													<a href="#/" className="widget-user-list-link">
														<img src="/color-admin/admin/react/assets/img/user/user-2.jpg" alt="" />
													</a>
												</div>
												<div className="widget-user-list-item">
													<a href="#/" className="widget-user-list-link">
														<img src="/color-admin/admin/react/assets/img/user/user-3.jpg" alt="" />
													</a>
												</div>
												<div className="widget-user-list-item">
													<a href="#/" className="widget-user-list-link">
														<img src="/color-admin/admin/react/assets/img/user/user-4.jpg" alt="" />
													</a>
												</div>
												<div className="widget-user-list-item">
													<a href="#/" className="widget-user-list-link">
														<img src="/color-admin/admin/react/assets/img/user/user-5.jpg" alt="" />
													</a>
												</div>
												<div className="widget-user-list-item">
													<a href="#/" className="widget-user-list-link">
														<img src="/color-admin/admin/react/assets/img/user/user-6.jpg" alt="" />
													</a>
												</div>
												<div className="widget-user-list-item">
													<a href="#/" className="widget-user-list-link">
														<img src="/color-admin/admin/react/assets/img/user/user-7.jpg" alt="" />
													</a>
												</div>
												<div className="widget-user-list-item">
													<a
														href="#/"
														className="widget-user-list-link bg-body text-body text-opacity-50 fs-12px fw-bold"
													>
														+26
													</a>
												</div>
											</div>
										</div>
										<div
											className="hljs-container hljs-wrapper rounded-0 rounded-bottom"
											data-bs-theme="dark"
										>
											<pre>
												<code
													className="xml"
													data-url="/color-admin/admin/react/assets/data/widgets/code-5.json"
												></code>
											</pre>
										</div>
									</div>
								</div>
								<div id="mapWidget" className="mb-5">
									<h4>Map widget</h4>
									<p>
										Map widget created with Bootstrap <code>.card</code> and{' '}
										<code>.list-group</code> component twitted with helper css classes.
									</p>
									<div className="card">
										<div className="card-body">
											<div className="row">
												<div className="col-xl-8">
													<div className="card rounded-3 overflow-hidden ">
														<div className="card-header bg-none fw-bold">Google Map</div>
								<iframe
									title="Google Maps location"
															src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d960.5886473867613!2d-122.41743634015282!3d37.776451983493104!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x8085809c6c8f4459%3A0xb10ed6d9b5050fa5!2sTwitter+HQ!5e0!3m2!1sen!2s!4v1495935122933"
															style={{ border: '0', width: '100%', height: '10rem' }}
															allowfullscreen
														></iframe>
														<div className="list-group list-group-flush">
															<div className="list-group-item d-flex align-items-center ">
																<div className="w-40px h-40px rounded-3 bg-primary text-white d-flex align-items-center justify-content-center">
																	<iconify-icon
																		icon="solar:map-point-wave-bold-duotone"
																		className="fs-4"
																	></iconify-icon>
																</div>
																<div className="flex-fill px-3">
																	<div className="fw-bold">via Road I-88E</div>
																	<div className="fw-semibold small">
																		Fastest route, the usual traffic
																	</div>
																</div>
																<div className="text-nowrap">
																	<div className="text-primary fw-bold">3h 54min</div>
																	<div className="fw-semibold small">393km</div>
																</div>
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
										<div
											className="hljs-container hljs-wrapper rounded-0 rounded-bottom"
											data-bs-theme="dark"
										>
											<pre>
												<code
													className="xml"
													data-url="/color-admin/admin/react/assets/data/widgets/code-6.json"
												></code>
											</pre>
										</div>
									</div>
								</div>
								<div id="chatWidget" className="mb-5">
									<h4>Chat widget</h4>
									<p>
										Chat widget created by using Bootstrap <code>.card</code> component with custom
										created bubble chat ui.
									</p>
									<div className="card">
										<div className="card-body">
											<div className="row">
												<div className="col-xl-8">
													<div className="card rounded-3 overflow-hidden ">
														<div className="card-header bg-none fw-bold d-flex align-items-center gap-2">
															<iconify-icon
																icon="solar:chat-dots-bold-duotone"
																className="fs-3 my-n1 text-secondary"
															></iconify-icon>
															Discussion group
															<a
																href="#/"
																className="ms-auto link-muted text-decoration-none"
																data-toggle="card-expand"
															>
																<i className="fa fa-expand"></i>
															</a>
														</div>
														<div
															className="card-body bg-light h-200px"
															data-scrollbar="true"
														>
															<div className="widget-chat">
																<div className="widget-chat-item">
																	<div className="widget-chat-media">
																		<img
																			src="/color-admin/admin/react/assets/img/user/profile.jpg"
																			alt=""
																		/>
																	</div>
																	<div className="widget-chat-content">
																		<div className="widget-chat-name">
																			Roberto Lambert
																		</div>
																		<div className="widget-chat-message last">
																			Hey, I'm testing out group messaging.
																		</div>
																	</div>
																</div>
																<div className="widget-chat-item reply">
																	<div className="widget-chat-content">
																		<div className="widget-chat-message last">
																			Cool
																		</div>
																		<div className="widget-chat-status">
																			<b>Read</b> 16:26
																		</div>
																	</div>
																</div>
																<div className="widget-chat-date">Today 14:21</div>
																<div className="widget-chat-item">
																	<div className="widget-chat-media">
																		<img
																			src="/color-admin/admin/react/assets/img/user/profile.jpg"
																			alt=""
																		/>
																	</div>
																	<div className="widget-chat-content">
																		<div className="widget-chat-name">
																			Rick Powell
																		</div>
																		<div className="widget-chat-message last">
																			Awesome! What's new?
																		</div>
																	</div>
																</div>
																<div className="widget-chat-item">
																	<div className="widget-chat-media">
																		<img
																			src="/color-admin/admin/react/assets/img/user/profile.jpg"
																			alt=""
																		/>
																	</div>
																	<div className="widget-chat-content">
																		<div className="widget-chat-name">
																			Roberto Lambert
																		</div>
																		<div className="widget-chat-message">
																			Not much, It's got a new look, contact pics
																			show up in group messaging, some other small
																			stuff.
																		</div>
																		<div className="widget-chat-message last">
																			How's crusty old iOS 6 treating you?
																		</div>
																	</div>
																</div>
																<div className="widget-chat-item reply">
																	<div className="widget-chat-content">
																		<div className="widget-chat-message last">
																			Sucks
																		</div>
																		<div className="widget-chat-status">
																			<b>Read</b> 16:30
																		</div>
																	</div>
																</div>
															</div>
														</div>
														<div className="card-footer bg-none d-flex gap-2">
<img
																src="/color-admin/admin/react/assets/img/user/profile.jpg"
																width="37px"
																height="37px"
																className="object-fit-cover rounded-pill"
															 alt="" />
															<div className="input-group">
																<input
																	type="text"
																	className="form-control border-end-0 rounded-pill rounded-end-0 ps-3"
																	placeholder="Write something..."
																/>
																<div className="input-group-text bg-none py-3px px-4px rounded-pill rounded-start-0">
																	<button
																		className="btn btn-primary w-80px btn-sm rounded-pill py-4px px-3"
																		type="button"
																	>
																		Send
																	</button>
																</div>
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
										<div
											className="hljs-container hljs-wrapper rounded-0 rounded-bottom"
											data-bs-theme="dark"
										>
											<pre>
												<code
													className="xml"
													data-url="/color-admin/admin/react/assets/data/widgets/code-7.json"
												></code>
											</pre>
										</div>
									</div>
								</div>
								<div id="profileWidget" className="mb-5">
									<h4>Profile widget</h4>
									<p>
										Profile widget created by using Bootstrap <code>.card</code> component with
										Bootstrap grid.
									</p>
									<div className="card">
										<div className="card-body">
											<div className="row">
												<div className="col-xl-8">
													<div className="card rounded-3 border-0 overflow-hidden ">
														<div className="position-relative">
															<img
																src="/color-admin/admin/react/assets/img/widget/cover-2.jpg"
																className="card-img rounded-0"
																alt=""
																height="120"
															/>
															<div className="card-img-overlay text-center text-white bg-black bg-opacity-60 d-flex align-items-start flex-column justify-content-end rounded-0 py-0">
																<div className="mb-n5">
																	<img
																		src="/color-admin/admin/react/assets/img/user/profile.jpg"
																		alt=""
																		width="100"
																		className="rounded-circle border border-2 border-white"
																	/>
																</div>
															</div>
														</div>
														<div className="card-body border border-top-0 pt-5 rounded-bottom-3">
															<div className="mb-3 pt-2">
																<div className="fs-5 fw-bold">
																	Alex Morgan{' '}
																	<i className="fa fa-circle-check text-primary"></i>
																</div>
																<div className="fw-semibold text-body text-opacity-50 mb-2">
																	Product Designer @ SeanTheme
																</div>
																<div className="fw-semibold mb-2">
																	Designing with purpose, driven by creativity, data,
																	and a love for solving real user problems.
																</div>
															</div>
															<div className="mb-3 d-flex fw-semibold gap-4 text-body text-opacity-50">
																<div>
																	<b className="text-body">68,499</b> followers
																</div>
																<div>
																	<b className="text-body">2,394</b> connections
																</div>
															</div>
															<div className="fw-semibold text-body text-opacity-50 mb-3">
																<i className="fa fa-map-marker-alt"></i>{' '}
																<span className="text-secondary">
																	San Francisco, CA
																</span>
															</div>
															<div className="d-flex gap-2">
																<a
																	href="#/"
																	className="btn btn-primary rounded-pill px-3 flex-fill"
																>
																	<i className="fa fa-user-plus me-1"></i>Follow
																</a>
																<a
																	href="#/"
																	className="btn btn-white rounded-pill px-3 flex-fill"
																>
																	<i className="fa fa-envelope me-1"></i>Message
																</a>
																<a href="#/" className="btn btn-white rounded-pill px-2">
																	<i className="fa fa-ellipsis"></i>
																</a>
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
										<div
											className="hljs-container hljs-wrapper rounded-0 rounded-bottom"
											data-bs-theme="dark"
										>
											<pre>
												<code
													className="xml"
													data-url="/color-admin/admin/react/assets/data/widgets/code-8.json"
												></code>
											</pre>
										</div>
									</div>
								</div>
								<div id="productWidget" className="mb-5">
									<h4>Product widget</h4>
									<p>
										Product widget created by using Bootstrap <code>.list-group</code> component
										with <code>.d-flex</code> utilities.
									</p>
									<div className="card">
										<div className="card-body">
											<div className="row">
												<div className="col-xl-8">
													<div className="list-group rounded-3">
														<a
															href="#/"
															className="list-group-item list-group-item-action d-flex align-items-center text-body "
														>
															<div className="w-80px h-80px d-flex align-items-center justify-content-center">
																<img
																	src="/color-admin/admin/react/assets/img/product/iphone-17-pro-max.jpg"
																	alt=""
																	className="w-100 h-100 object-fit-cover rounded-3"
																/>
															</div>
															<div className="flex-fill px-3 py-1">
																<div className="fw-bold">iPhone 17 Pro Max 1TB</div>
																<div className="fw-semibold text-secondary mb-2">
																	Apple
																</div>
																<div className="d-flex align-items-center">
																	<i className="fa fa-star text-warning"></i>
																	<i className="fa fa-star text-warning"></i>
																	<i className="fa fa-star text-warning"></i>
																	<i className="fa fa-star text-warning"></i>
																	<i className="fa fa-star text-warning me-2"></i>
																	<span className="fw-bold small">(542)</span>
																</div>
															</div>
															<div className="fw-bold text-body">$1,599.00</div>
														</a>
														<a
															href="#/"
															className="list-group-item list-group-item-action d-flex align-items-center text-body p-3 "
														>
															<div className="w-80px h-80px d-flex align-items-center justify-content-center">
																<img
																	src="/color-admin/admin/react/assets/img/product/ipad-pro.jpg"
																	alt=""
																	className="w-100 h-100 object-fit-cover rounded-3"
																/>
															</div>
															<div className="flex-fill px-3 py-1">
																<div className="fw-bold">iPad Pro 13-inch 1TB</div>
																<div className="fw-semibold text-secondary mb-2">
																	Apple
																</div>
																<div className="d-flex align-items-center">
																	<i className="fa fa-star text-warning"></i>
																	<i className="fa fa-star text-warning"></i>
																	<i className="fa fa-star text-warning"></i>
																	<i className="fa fa-star text-warning"></i>
																	<i className="far fa-star text-body text-opacity-25 me-2"></i>
																	<span className="fw-bold small">(128)</span>
																</div>
															</div>
															<div className="fw-bold text-body">$1,899.00</div>
														</a>
														<a
															href="#/"
															className="list-group-item list-group-item-action d-flex align-items-center text-body p-3 "
														>
															<div className="w-80px h-80px d-flex align-items-center justify-content-center">
																<img
																	src="/color-admin/admin/react/assets/img/product/apple-watch-ultra-3.jpg"
																	alt=""
																	className="w-100 h-100 object-fit-cover rounded-3"
																/>
															</div>
															<div className="flex-fill px-3 py-1">
																<div className="fw-bold">Apple Watch Ultra 3</div>
																<div className="fw-semibold text-secondary mb-2">
																	Apple
																</div>
																<div className="d-flex align-items-center">
																	<i className="fa fa-star text-warning"></i>
																	<i className="fa fa-star text-warning"></i>
																	<i className="fa fa-star text-warning"></i>
																	<i className="fa fa-star text-warning"></i>
																	<i className="far fa-star text-body text-opacity-25 me-2"></i>
																	<span className="fw-bold small">(65)</span>
																</div>
															</div>
															<div className="fw-bold text-body">$799.00</div>
														</a>
													</div>
												</div>
											</div>
										</div>
										<div
											className="hljs-container hljs-wrapper rounded-0 rounded-bottom"
											data-bs-theme="dark"
										>
											<pre>
												<code
													className="xml"
													data-url="/color-admin/admin/react/assets/data/widgets/code-9.json"
												></code>
											</pre>
										</div>
									</div>
								</div>
								<div id="reminderWidget" className="mb-5">
									<h4>Reminder widget</h4>
									<p>
										Reminder widget used to create a simple calendar to notify the user upcoming
										events or task.
									</p>
									<div className="card">
										<div className="card-body">
											<div className="row">
												<div className="col-xl-8">
													<div className="card rounded-3 ">
														<div className="card-header fw-bold gap-2 bg-none">
															<iconify-icon
																icon="solar:calendar-bold-duotone"
																className="fs-4"
															></iconify-icon>{' '}
															Today, Nov 4
														</div>
														<div className="widget-reminder">
															<div className="widget-reminder-item">
																<div className="widget-reminder-time">
																	09:00
																	<br />
																	12:00
																</div>
																<div className="widget-reminder-divider bg-secondary"></div>
																<div className="widget-reminder-content">
																	<div className="fw-bold">Meeting with HR</div>
																	<div className="small fw-semibold">
																		{' '}
																		- Conference Room
																	</div>
																</div>
															</div>
															<div className="widget-reminder-item">
																<div className="widget-reminder-time">
																	20:00
																	<br />
																	23:00
																</div>
																<div className="widget-reminder-divider"></div>
																<div className="widget-reminder-content">
																	<div className="fw-bold">Dinner with Richard</div>
																	<div className="small fw-semibold">
																		{' '}
																		- Tom's Too Restaurant
																	</div>
																	<div className="d-flex align-items-center mt-2 fw-semibold small">
																		<div className="flex-fill d-flex align-items-center">
																			<img
																				src="/color-admin/admin/react/assets/img/user/profile.jpg"
																				alt=""
																				width="20"
																				className="rounded-circle me-2"
																			/>{' '}
																			Richard Leong
																		</div>
																		<a href="#/" className="ms-auto">
																			Contact
																		</a>
																	</div>
																</div>
															</div>
														</div>
														<div className="card-header fw-bold gap-2 bg-none border-top">
															<iconify-icon
																icon="solar:calendar-bold-duotone"
																className="fs-4"
															></iconify-icon>{' '}
															Tomorrow, Nov 5
														</div>
														<div className="widget-reminder">
															<div className="widget-reminder-item">
																<div className="widget-reminder-time">All day</div>
																<div className="widget-reminder-divider"></div>
																<div className="widget-reminder-content">
																	<div className="fw-bold">Terry Birthday</div>
																</div>
															</div>
															<div className="widget-reminder-item">
																<div className="widget-reminder-time">08:00</div>
																<div className="widget-reminder-divider"></div>
																<div className="widget-reminder-content">
																	<div className="fw-bold">Meeting</div>
																</div>
															</div>
															<div className="widget-reminder-item">
																<div className="widget-reminder-time">
																	00:00
																	<br />
																	00:30
																</div>
																<div className="widget-reminder-divider"></div>
																<div className="widget-reminder-content">
																	<div className="fw-bold">Server Maintenance</div>
																	<div className="fw-semibold small">
																		{' '}
																		- Data Centre
																	</div>
																</div>
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
										<div
											className="hljs-container hljs-wrapper rounded-0 rounded-bottom"
											data-bs-theme="dark"
										>
											<pre>
												<code
													className="xml"
													data-url="/color-admin/admin/react/assets/data/widgets/code-10.json"
												></code>
											</pre>
										</div>
									</div>
								</div>
								<div id="imageListWidget" className="mb-5">
									<h4>Image list widget</h4>
									<p>
										Image list widget created by using Bootstrap <code>.card</code> and{' '}
										<code>.list-group</code> component with <code>.d-flex</code> utilities.
									</p>
									<div className="card">
										<div className="card-body">
											<div className="row">
												<div className="col-xl-8">
													<div
														className="card rounded-3 overflow-hidden card-borderless "
														data-bs-theme="dark"
													>
														<div className="list-group list-group-flush">
															<a
																href="#/"
																className="list-group-item list-group-item-action d-flex gap-3 align-items-center text-body rounded-3 rounded-bottom-0 p-3 bg-gray-900"
															>
																<div className="w-40px h-40px bg-theme-gradient text-black text-opacity-75 rounded-3 d-flex align-items-center justify-content-center">
																	<iconify-icon
																		icon="solar:leaf-bold-duotone"
																		className="fs-2"
																	></iconify-icon>
																</div>
																<div className="flex-fill">
																	<div className="fw-bold">Library (20)</div>
																	<div className="small fw-semibold text-body text-opacity-50">
																		3,192 Image Found
																	</div>
																</div>
																<div>
																	<i className="fa fa-chevron-right fa-lg text-body text-opacity-50"></i>
																</div>
															</a>
														</div>
														<div className="card-body2">
															<div className="widget-img-list">
																<div className="widget-img-list-item">
																	<a
																		href="/color-admin/admin/react/assets/img/gallery/gallery-1.jpg"
																		data-lity
																	>
																		<span
																			className="img"
																			style={{
																				backgroundImage:
																					'url(/color-admin/admin/react/assets/img/gallery/gallery-1.jpg)'
																			}}
																		></span>
																	</a>
																</div>
																<div className="widget-img-list-item">
																	<a
																		href="/color-admin/admin/react/assets/img/gallery/gallery-2.jpg"
																		data-lity
																	>
																		<span
																			className="img"
																			style={{
																				backgroundImage:
																					'url(/color-admin/admin/react/assets/img/gallery/gallery-2.jpg)'
																			}}
																		></span>
																	</a>
																</div>
																<div className="widget-img-list-item">
																	<a
																		href="/color-admin/admin/react/assets/img/gallery/gallery-3.jpg"
																		data-lity
																	>
																		<span
																			className="img"
																			style={{
																				backgroundImage:
																					'url(/color-admin/admin/react/assets/img/gallery/gallery-3.jpg)'
																			}}
																		></span>
																	</a>
																</div>
																<div className="widget-img-list-item">
																	<a
																		href="/color-admin/admin/react/assets/img/gallery/gallery-4.jpg"
																		data-lity
																	>
																		<span
																			className="img"
																			style={{
																				backgroundImage:
																					'url(/color-admin/admin/react/assets/img/gallery/gallery-4.jpg)'
																			}}
																		></span>
																	</a>
																</div>
																<div className="widget-img-list-item">
																	<a
																		href="/color-admin/admin/react/assets/img/gallery/gallery-5.jpg"
																		data-lity
																	>
																		<span
																			className="img"
																			style={{
																				backgroundImage:
																					'url(/color-admin/admin/react/assets/img/gallery/gallery-5.jpg)'
																			}}
																		></span>
																	</a>
																</div>
																<div className="widget-img-list-item">
																	<a
																		href="/color-admin/admin/react/assets/img/gallery/gallery-21.jpg"
																		data-lity
																	>
																		<span
																			className="img"
																			style={{
																				backgroundImage:
																					'url(/color-admin/admin/react/assets/img/gallery/gallery-21.jpg)'
																			}}
																		></span>
																	</a>
																</div>
																<div className="widget-img-list-item">
																	<a
																		href="/color-admin/admin/react/assets/img/gallery/gallery-22.jpg"
																		data-lity
																	>
																		<span
																			className="img"
																			style={{
																				backgroundImage:
																					'url(/color-admin/admin/react/assets/img/gallery/gallery-22.jpg)'
																			}}
																		></span>
																	</a>
																</div>
																<div className="widget-img-list-item">
																	<a
																		href="/color-admin/admin/react/assets/img/gallery/gallery-23.jpg"
																		data-lity
																	>
																		<span
																			className="img"
																			style={{
																				backgroundImage:
																					'url(/color-admin/admin/react/assets/img/gallery/gallery-23.jpg)'
																			}}
																		></span>
																	</a>
																</div>
																<div className="widget-img-list-item">
																	<a
																		href="/color-admin/admin/react/assets/img/gallery/gallery-24.jpg"
																		data-lity
																	>
																		<span
																			className="img"
																			style={{
																				backgroundImage:
																					'url(/color-admin/admin/react/assets/img/gallery/gallery-24.jpg)'
																			}}
																		></span>
																	</a>
																</div>
																<div className="widget-img-list-item">
																	<a
																		href="/color-admin/admin/react/assets/img/gallery/gallery-25.jpg"
																		data-lity
																	>
																		<span
																			className="img"
																			style={{
																				backgroundImage:
																					'url(/color-admin/admin/react/assets/img/gallery/gallery-25.jpg)'
																			}}
																		></span>
																	</a>
																</div>
																<div className="widget-img-list-item">
																	<a
																		href="/color-admin/admin/react/assets/img/gallery/gallery-26.jpg"
																		data-lity
																	>
																		<span
																			className="img"
																			style={{
																				backgroundImage:
																					'url(/color-admin/admin/react/assets/img/gallery/gallery-26.jpg)'
																			}}
																		></span>
																	</a>
																</div>
																<div className="widget-img-list-item">
																	<a
																		href="/color-admin/admin/react/assets/img/gallery/gallery-27.jpg"
																		data-lity
																	>
																		<span
																			className="img"
																			style={{
																				backgroundImage:
																					'url(/color-admin/admin/react/assets/img/gallery/gallery-27.jpg)'
																			}}
																		></span>
																	</a>
																</div>
																<div className="widget-img-list-item">
																	<a
																		href="/color-admin/admin/react/assets/img/gallery/gallery-28.jpg"
																		data-lity
																	>
																		<span
																			className="img"
																			style={{
																				backgroundImage:
																					'url(/color-admin/admin/react/assets/img/gallery/gallery-28.jpg)'
																			}}
																		></span>
																	</a>
																</div>
																<div className="widget-img-list-item">
																	<a
																		href="/color-admin/admin/react/assets/img/gallery/gallery-29.jpg"
																		data-lity
																	>
																		<span
																			className="img"
																			style={{
																				backgroundImage:
																					'url(/color-admin/admin/react/assets/img/gallery/gallery-29.jpg)'
																			}}
																		></span>
																	</a>
																</div>
																<div className="widget-img-list-item">
																	<a
																		href="/color-admin/admin/react/assets/img/gallery/gallery-30.jpg"
																		data-lity
																	>
																		<span
																			className="img"
																			style={{
																				backgroundImage:
																					'url(/color-admin/admin/react/assets/img/gallery/gallery-30.jpg)'
																			}}
																		></span>
																	</a>
																</div>
																<div className="widget-img-list-item">
																	<a
																		href="/color-admin/admin/react/assets/img/gallery/gallery-31.jpg"
																		data-lity
																	>
																		<span
																			className="img"
																			style={{
																				backgroundImage:
																					'url(/color-admin/admin/react/assets/img/gallery/gallery-31.jpg)'
																			}}
																		></span>
																	</a>
																</div>
																<div className="widget-img-list-item">
																	<a
																		href="/color-admin/admin/react/assets/img/gallery/gallery-32.jpg"
																		data-lity
																	>
																		<span
																			className="img"
																			style={{
																				backgroundImage:
																					'url(/color-admin/admin/react/assets/img/gallery/gallery-32.jpg)'
																			}}
																		></span>
																	</a>
																</div>
																<div className="widget-img-list-item">
																	<a
																		href="/color-admin/admin/react/assets/img/gallery/gallery-33.jpg"
																		data-lity
																	>
																		<span
																			className="img"
																			style={{
																				backgroundImage:
																					'url(/color-admin/admin/react/assets/img/gallery/gallery-33.jpg)'
																			}}
																		></span>
																	</a>
																</div>
																<div className="widget-img-list-item">
																	<a
																		href="/color-admin/admin/react/assets/img/gallery/gallery-34.jpg"
																		data-lity
																	>
																		<span
																			className="img"
																			style={{
																				backgroundImage:
																					'url(/color-admin/admin/react/assets/img/gallery/gallery-34.jpg)'
																			}}
																		></span>
																	</a>
																</div>
																<div className="widget-img-list-item">
																	<a
																		href="/color-admin/admin/react/assets/img/gallery/gallery-35.jpg"
																		data-lity
																	>
																		<span
																			className="img"
																			style={{
																				backgroundImage:
																					'url(/color-admin/admin/react/assets/img/gallery/gallery-35.jpg)'
																			}}
																		></span>
																	</a>
																</div>
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
										<div
											className="hljs-container hljs-wrapper rounded-0 rounded-bottom"
											data-bs-theme="dark"
										>
											<pre>
												<code
													className="xml"
													data-url="/color-admin/admin/react/assets/data/widgets/code-11.json"
												></code>
											</pre>
										</div>
									</div>
								</div>
								<div id="todolistWidget" className="mb-5">
									<h4>Todolist widget</h4>
									<p>
										To-Do List widget created by using Bootstrap <code>.list-group</code> and{' '}
										<code>.form-check</code> components with <code>.d-flex</code> and{' '}
										<code>.align-items-center</code> utilities for layout alignment.
									</p>
									<div className="card">
										<div className="card-body">
											<div className="row">
												<div className="col-xl-8">
													<div className="card rounded-3 overflow-hidden ">
														<div className="card-header d-flex align-items-center justify-content-between bg-none">
															<div className="fs-5 fw-bold flex-fill">Todolist</div>
															<div className="text-center">
																<div className="fw-bolder fs-5 lh-1">0</div>
																<div className="fw-semibold small lh-14 text-secondary">
																	Done
																</div>
															</div>
														</div>
														<div className="list-group list-group-flush">
															<div className="list-group-item d-flex align-items-center ">
																<div className="form-check m-0 flex-fill">
																	<input
																		className="form-check-input"
																		id="tdCheckbox1"
																		type="checkbox"
																		value=""
																	/>
																	<label
																		className="form-check-label ps-2"
																		htmlFor="tdCheckbox1"
																	>
																		<span className="d-block fw-bold form-check-line-through">
																			Design new landing page
																		</span>
																		<span className="d-block fw-semibold small text-secondary">
																			Create a modern hero section and update
																			call-to-action.
																		</span>
																	</label>
																</div>
																<div>
																	<a href="#/" className="text-decoration-none">
																		<iconify-icon
																			icon="solar:question-circle-bold-duotone"
																			className="fs-3"
																		></iconify-icon>
																	</a>
																</div>
															</div>
															<div className="list-group-item d-flex align-items-center ">
																<div className="form-check m-0 flex-fill">
																	<input
																		className="form-check-input"
																		id="tdCheckbox2"
																		type="checkbox"
																		value=""
																	/>
																	<label
																		className="form-check-label ps-2"
																		htmlFor="tdCheckbox2"
																	>
																		<span className="d-block fw-bold form-check-line-through">
																			Prepare presentation slides
																		</span>
																		<span className="d-block fw-semibold small text-secondary">
																			Add product roadmap and new feature
																			highlights.
																		</span>
																	</label>
																</div>
																<div>
																	<a href="#/" className="text-decoration-none">
																		<iconify-icon
																			icon="solar:question-circle-bold-duotone"
																			className="fs-3"
																		></iconify-icon>
																	</a>
																</div>
															</div>
															<div className="list-group-item d-flex align-items-center ">
																<div className="form-check m-0 flex-fill">
																	<input
																		className="form-check-input"
																		id="tdCheckbox3"
																		type="checkbox"
																		value=""
																	/>
																	<label
																		className="form-check-label ps-2"
																		htmlFor="tdCheckbox3"
																	>
																		<span className="d-block fw-bold form-check-line-through">
																			Team meeting at 3 PM
																		</span>
																		<span className="d-block fw-semibold small text-secondary">
																			Discuss Q4 goals and assign tasks.
																		</span>
																	</label>
																</div>
																<div>
																	<a href="#/" className="text-decoration-none">
																		<iconify-icon
																			icon="solar:question-circle-bold-duotone"
																			className="fs-3"
																		></iconify-icon>
																	</a>
																</div>
															</div>
															<div className="list-group-item d-flex align-items-center ">
																<div className="form-check m-0 flex-fill">
																	<input
																		className="form-check-input"
																		id="tdCheckbox4"
																		type="checkbox"
																		value=""
																	/>
																	<label
																		className="form-check-label ps-2"
																		htmlFor="tdCheckbox4"
																	>
																		<span className="d-block fw-bold form-check-line-through">
																			<b className="text-warning">!!</b> Send
																			invoice to client
																		</span>
																		<span className="d-block fw-semibold small text-secondary">
																			Include last month’s hours and additional
																			revisions.
																		</span>
																	</label>
																</div>
																<div>
																	<a href="#/" className="text-decoration-none">
																		<iconify-icon
																			icon="solar:question-circle-bold-duotone"
																			className="fs-3"
																		></iconify-icon>
																	</a>
																</div>
															</div>
														</div>
														<div className="card-footer d-flex align-items-center gap-2 bg-none p-3">
															<i className="fa fa-plus fa-lg opacity-5 w-20px me-2"></i>
															<div className="flex-fill">
																<input
																	type="text"
																	className="form-control border-0 shadow-none rounded-0 p-0 h-25px bg-none fw-semibold"
																	placeholder="Write your task here..."
																/>
															</div>
															<div>
																<button
																	type="button"
																	className="btn btn-primary btn-sm rounded-pill px-3 my-n2"
																>
																	Add Task
																</button>
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
										<div
											className="hljs-container hljs-wrapper rounded-0 rounded-bottom"
											data-bs-theme="dark"
										>
											<pre>
												<code
													className="xml"
													data-url="/color-admin/admin/react/assets/data/widgets/code-12.json"
												></code>
											</pre>
										</div>
									</div>
								</div>
								<div id="squareWidget" className="mb-5">
									<h4>Square widget</h4>
									<p>
										Square widget is a flexible content block designed with equal width and height
										proportions. It's ideal for displaying concise information such as quick stats,
										icons, or shortcuts. Built using Bootstrap's <code>.card</code> component,{' '}
										<code>.ratio</code> utility, and responsive flexbox alignment classes for
										consistent sizing across all devices.
									</p>
									<div className="card">
										<div className="card-body">
											<div className="row mb-4">
												<div className="col-xs-6 col-sm-3">
													<div className="row g-1 mb-1">
														<div className="col-6 d-flex flex-column gap-1">
															<a href="#/" className="ratio ratio-1x1">
<img
																	src="/color-admin/admin/react/assets/img/login-bg/login-bg-1-thumb.jpg"
																	className="object-fit-cover rounded"
																 alt="" />
															</a>
															<a href="#/" className="ratio ratio-1x1">
<img
																	src="/color-admin/admin/react/assets/img/login-bg/login-bg-2-thumb.jpg"
																	className="object-fit-cover rounded"
																 alt="" />
															</a>
														</div>
														<div className="col-6 d-flex flex-column gap-1">
															<a href="#/" className="ratio ratio-1x1">
<img
																	src="/color-admin/admin/react/assets/img/login-bg/login-bg-3-thumb.jpg"
																	className="object-fit-cover rounded"
																 alt="" />
															</a>
															<a href="#/" className="ratio ratio-1x1">
<img
																	src="/color-admin/admin/react/assets/img/login-bg/login-bg-4-thumb.jpg"
																	className="object-fit-cover rounded"
																 alt="" />
															</a>
														</div>
													</div>
													<div className="small fw-bold">Camera Roll</div>
													<div className="small fw-semibold text-body text-opacity-50">
														2,711
													</div>
												</div>
												<div className="col-xs-6 col-sm-3">
													<div className="mb-1">
														<a href="#/" className="ratio ratio-1x1">
<img
																src="/color-admin/admin/react/assets/img/login-bg/login-bg-7-thumb.jpg"
																className="object-fit-cover rounded"
															 alt="" />
															<div className="h-100 w-100 d-flex align-items-center justify-content-center bg-black bg-opacity-25 rounded">
																<i className="fa fa-play fa-3x text-white"></i>
															</div>
														</a>
													</div>
													<div className="small fw-bold">Videos</div>
													<div className="small fw-semibold text-body text-opacity-50">
														31
													</div>
												</div>
												<div className="col-xs-6 col-sm-3">
													<div className="mb-1">
														<a href="#/" className="ratio ratio-1x1">
<img
																src="/color-admin/admin/react/assets/img/login-bg/login-bg-10-thumb.jpg"
																className="object-fit-cover rounded"
															 alt="" />
															<div className="h-100 w-100 d-flex align-items-center justify-content-center bg-black bg-opacity-25 rounded">
																<div className="text-center">
																	<div className="text-white fw-bold">
																		PORTRAITS OF
																	</div>
																	<div className="text-white text-opacity-75 fw-bold">
																		2026
																	</div>
																</div>
															</div>
														</a>
													</div>
													<div className="small fw-bold">Memory 2026</div>
													<div className="small fw-semibold text-body text-opacity-50">
														1,239
													</div>
												</div>
												<div className="col-xs-6 col-sm-3">
													<div className="row gx-1 mb-5px">
														<div className="col-3 d-flex flex-wrap flex-column gap-1">
															<a href="#/" className="ratio ratio-1x1">
<img
																	src="/color-admin/admin/react/assets/img/gallery/gallery-1-thumb.jpg"
																	className="object-fit-cover rounded"
																 alt="" />
															</a>
															<a href="#/" className="ratio ratio-1x1">
<img
																	src="/color-admin/admin/react/assets/img/gallery/gallery-2-thumb.jpg"
																	className="object-fit-cover rounded"
																 alt="" />
															</a>
															<a href="#/" className="ratio ratio-1x1">
<img
																	src="/color-admin/admin/react/assets/img/gallery/gallery-3-thumb.jpg"
																	className="object-fit-cover rounded"
																 alt="" />
															</a>
															<a href="#/" className="ratio ratio-1x1">
<img
																	src="/color-admin/admin/react/assets/img/gallery/gallery-4-thumb.jpg"
																	className="object-fit-cover rounded"
																 alt="" />
															</a>
														</div>
														<div className="col-3 d-flex flex-wrap flex-column gap-1">
															<a href="#/" className="ratio ratio-1x1">
<img
																	src="/color-admin/admin/react/assets/img/gallery/gallery-5-thumb.jpg"
																	className="object-fit-cover rounded"
																 alt="" />
															</a>
															<a href="#/" className="ratio ratio-1x1">
<img
																	src="/color-admin/admin/react/assets/img/gallery/gallery-6-thumb.jpg"
																	className="object-fit-cover rounded"
																 alt="" />
															</a>
															<a href="#/" className="ratio ratio-1x1">
<img
																	src="/color-admin/admin/react/assets/img/gallery/gallery-7-thumb.jpg"
																	className="object-fit-cover rounded"
																 alt="" />
															</a>
															<a href="#/" className="ratio ratio-1x1">
<img
																	src="/color-admin/admin/react/assets/img/gallery/gallery-8-thumb.jpg"
																	className="object-fit-cover rounded"
																 alt="" />
															</a>
														</div>
														<div className="col-3 d-flex flex-wrap flex-column gap-1">
															<a href="#/" className="ratio ratio-1x1">
<img
																	src="/color-admin/admin/react/assets/img/gallery/gallery-9-thumb.jpg"
																	className="object-fit-cover rounded"
																 alt="" />
															</a>
															<a href="#/" className="ratio ratio-1x1">
<img
																	src="/color-admin/admin/react/assets/img/gallery/gallery-10-thumb.jpg"
																	className="object-fit-cover rounded"
																 alt="" />
															</a>
															<a href="#/" className="ratio ratio-1x1">
<img
																	src="/color-admin/admin/react/assets/img/gallery/gallery-11-thumb.jpg"
																	className="object-fit-cover rounded"
																 alt="" />
															</a>
															<a href="#/" className="ratio ratio-1x1">
<img
																	src="/color-admin/admin/react/assets/img/gallery/gallery-12-thumb.jpg"
																	className="object-fit-cover rounded"
																 alt="" />
															</a>
														</div>
														<div className="col-3 d-flex flex-wrap flex-column gap-1">
															<a href="#/" className="ratio ratio-1x1">
<img
																	src="/color-admin/admin/react/assets/img/gallery/gallery-13-thumb.jpg"
																	className="object-fit-cover rounded"
																 alt="" />
															</a>
															<a href="#/" className="ratio ratio-1x1">
<img
																	src="/color-admin/admin/react/assets/img/gallery/gallery-14-thumb.jpg"
																	className="object-fit-cover rounded"
																 alt="" />
															</a>
															<a href="#/" className="ratio ratio-1x1">
<img
																	src="/color-admin/admin/react/assets/img/gallery/gallery-15-thumb.jpg"
																	className="object-fit-cover rounded"
																 alt="" />
															</a>
															<a href="#/" className="ratio ratio-1x1">
<img
																	src="/color-admin/admin/react/assets/img/gallery/gallery-16-thumb.jpg"
																	className="object-fit-cover rounded"
																 alt="" />
															</a>
														</div>
													</div>
													<div className="small fw-bold">Albums</div>
													<div className="small fw-semibold text-body text-opacity-50">8</div>
												</div>
											</div>
											<div className="row mb-4">
												<div className="col-sm-6">
													<div className="d-flex flex-wrap gap-1 mb-1">
														<div className="ratio ratio-1x1 rounded bg-indigo text-white w-40px h-40px">
															<div className="d-flex align-items-center justify-content-center">
																<i className="fab fa-digital-ocean fa-2x"></i>
															</div>
														</div>
														<div className="ratio ratio-1x1 rounded bg-blue text-white w-40px h-40px">
															<div className="d-flex align-items-center justify-content-center">
																<i className="fab fa-twitter fa-2x"></i>
															</div>
														</div>
														<div className="ratio ratio-1x1 rounded bg-success text-white w-40px h-40px">
															<div className="d-flex align-items-center justify-content-center">
																<i className="fab fa-android fa-2x"></i>
															</div>
														</div>
														<div className="ratio ratio-1x1 rounded bg-warning text-white w-40px h-40px">
															<div className="d-flex align-items-center justify-content-center">
																<i className="fab fa-firefox fa-2x"></i>
															</div>
														</div>
														<div className="ratio ratio-1x1 rounded bg-danger text-white w-40px h-40px">
															<div className="d-flex align-items-center justify-content-center">
																<i className="fab fa-google-plus-g fa-2x"></i>
															</div>
														</div>
														<div className="ratio ratio-1x1 rounded bg-pink text-white w-40px h-40px">
															<div className="d-flex align-items-center justify-content-center">
																<i className="fab fa-pinterest fa-2x"></i>
															</div>
														</div>
													</div>
													<div className="d-flex flex-wrap gap-1 mb-1">
														<div className="ratio ratio-1x1 rounded bg-secondary bg-opacity-50 text-white w-70px h-70px overflow-hidden">
															<div className="d-flex align-items-center justify-content-center">
																<i className="fa fa-user fs-60px mt-15px pt-3px"></i>
															</div>
														</div>
														<div className="ratio ratio-1x1 rounded bg-secondary bg-opacity-50 text-white w-60px h-60px overflow-hidden">
															<div className="d-flex align-items-center justify-content-center">
																<i className="fa fa-user fs-48px mt-10px pt-3px"></i>
															</div>
														</div>
														<div className="ratio ratio-1x1 rounded bg-secondary bg-opacity-50 text-white w-50px h-50px overflow-hidden">
															<div className="d-flex align-items-center justify-content-center">
																<i className="fa fa-user fs-40px mt-10px pt-2px"></i>
															</div>
														</div>
														<div className="ratio ratio-1x1 rounded bg-secondary bg-opacity-50 text-white w-40px h-40px overflow-hidden">
															<div className="d-flex align-items-center justify-content-center">
																<i className="fa fa-user fs-32px mt-10px pt-1px"></i>
															</div>
														</div>
														<div className="ratio ratio-1x1 rounded bg-secondary bg-opacity-50 text-white w-30px h-30px overflow-hidden">
															<div className="d-flex align-items-center justify-content-center">
																<i className="fa fa-user fs-24px mt-9px"></i>
															</div>
														</div>
													</div>
												</div>
												<div className="col-sm-6">
													<div className="d-flex flex-wrap gap-1 mb-1">
														<div className="ratio ratio-1x1 rounded bg-dark w-40px h-40px overflow-hidden">
<img
																src="/color-admin/admin/react/assets/img/gallery/gallery-11-thumb.jpg"
																className="object-fit-cover"
															 alt="" />
														</div>
														<div className="ratio ratio-1x1 rounded bg-dark w-40px h-40px overflow-hidden">
<img
																src="/color-admin/admin/react/assets/img/gallery/gallery-12-thumb.jpg"
																className="object-fit-cover"
															 alt="" />
														</div>
														<div className="ratio ratio-1x1 rounded bg-dark w-40px h-40px overflow-hidden">
<img
																src="/color-admin/admin/react/assets/img/gallery/gallery-13-thumb.jpg"
																className="object-fit-cover"
															 alt="" />
														</div>
														<div className="ratio ratio-1x1 rounded bg-dark w-40px h-40px overflow-hidden">
<img
																src="/color-admin/admin/react/assets/img/gallery/gallery-14-thumb.jpg"
																className="object-fit-cover"
															 alt="" />
														</div>
														<div className="ratio ratio-1x1 rounded bg-dark w-40px h-40px overflow-hidden">
<img
																src="/color-admin/admin/react/assets/img/gallery/gallery-15-thumb.jpg"
																className="object-fit-cover"
															 alt="" />
														</div>
														<div className="ratio ratio-1x1 rounded bg-dark w-40px h-40px overflow-hidden">
<img
																src="/color-admin/admin/react/assets/img/gallery/gallery-16-thumb.jpg"
																className="object-fit-cover"
															 alt="" />
														</div>
													</div>
													<div className="d-flex flex-wrap gap-1 mb-1">
														<div className="ratio ratio-1x1 rounded bg-dark w-70px h-70px overflow-hidden">
<img
																src="/color-admin/admin/react/assets/img/gallery/gallery-21-thumb.jpg"
																className="object-fit-cover"
															 alt="" />
														</div>
														<div className="ratio ratio-1x1 rounded bg-dark w-60px h-60px overflow-hidden">
<img
																src="/color-admin/admin/react/assets/img/gallery/gallery-22-thumb.jpg"
																className="object-fit-cover"
															 alt="" />
														</div>
														<div className="ratio ratio-1x1 rounded bg-dark w-50px h-50px overflow-hidden">
<img
																src="/color-admin/admin/react/assets/img/gallery/gallery-23-thumb.jpg"
																className="object-fit-cover"
															 alt="" />
														</div>
														<div className="ratio ratio-1x1 rounded bg-dark w-40px h-40px overflow-hidden">
<img
																src="/color-admin/admin/react/assets/img/gallery/gallery-24-thumb.jpg"
																className="object-fit-cover"
															 alt="" />
														</div>
														<div className="ratio ratio-1x1 rounded bg-dark w-30px h-30px overflow-hidden">
<img
																src="/color-admin/admin/react/assets/img/gallery/gallery-25-thumb.jpg"
																className="object-fit-cover"
															 alt="" />
														</div>
													</div>
												</div>
											</div>
										</div>
										<div
											className="hljs-container hljs-wrapper rounded-0 rounded-bottom"
											data-bs-theme="dark"
										>
											<pre>
												<code
													className="xml"
													data-url="/color-admin/admin/react/assets/data/widgets/code-13.json"
												></code>
											</pre>
										</div>
									</div>
								</div>
							</div>
							<div className="col-xl-3">
								<nav
									data-init="scrollspy"
									className="navbar navbar-sticky navbar-sticky-start d-none d-xl-block py-4 h-100"
								>
									<nav className="nav">
										<a className="nav-link active" href="#cardWidget" data-toggle="scroll-to">
											Card widget
										</a>
										<a className="nav-link" href="#listWidget" data-toggle="scroll-to">
											List widget
										</a>
										<a className="nav-link" href="#statsWidget" data-toggle="scroll-to">
											Stats widget
										</a>
										<a className="nav-link" href="#chartWidget" data-toggle="scroll-to">
											Chart widget
										</a>
										<a className="nav-link" href="#userListWidget" data-toggle="scroll-to">
											User list widget
										</a>
										<a className="nav-link" href="#mapWidget" data-toggle="scroll-to">
											Map widget
										</a>
										<a className="nav-link" href="#chatWidget" data-toggle="scroll-to">
											Chat widget
										</a>
										<a className="nav-link" href="#profileWidget" data-toggle="scroll-to">
											Profile widget
										</a>
										<a className="nav-link" href="#productWidget" data-toggle="scroll-to">
											Product widget
										</a>
										<a className="nav-link" href="#reminderWidget" data-toggle="scroll-to">
											Reminder widget
										</a>
										<a className="nav-link" href="#imageListWidget" data-toggle="scroll-to">
											Image list widget
										</a>
										<a className="nav-link" href="#todolistWidget" data-toggle="scroll-to">
											Todolist Widget
										</a>
									</nav>
								</nav>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default Widgets;
